import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLandingTheme } from '../hooks/useLandingTheme';

interface Testimonial {
  quoteKey: string;
  nameKey: string;
  titleKey: string;
  avatar: string;
}

const testimonials: Testimonial[] = [
  {
    quoteKey: 'landing.testimonials.testimonial1.quote',
    nameKey: 'landing.testimonials.testimonial1.name',
    titleKey: 'landing.testimonials.testimonial1.title',
    avatar: 'https://randomuser.me/api/portraits/women/32.jpg',
  },
  {
    quoteKey: 'landing.testimonials.testimonial2.quote',
    nameKey: 'landing.testimonials.testimonial2.name',
    titleKey: 'landing.testimonials.testimonial2.title',
    avatar: 'https://randomuser.me/api/portraits/men/45.jpg',
  },
  {
    quoteKey: 'landing.testimonials.testimonial3.quote',
    nameKey: 'landing.testimonials.testimonial3.name',
    titleKey: 'landing.testimonials.testimonial3.title',
    avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
  },
];

const TestimonialsSection: React.FC = () => {
  const { t } = useTranslation();
  const isDark = useLandingTheme();

  return (
    <section
      id="testimonials"
      className="py-16 md:py-32"
      style={{
        background: isDark ? '#1D293D' : '#F8FAFC',
      }}
    >
      <div
        className="max-w-[1200px] mx-auto px-6 lg:px-0 flex flex-col items-center"
        style={{ gap: '48px' }}
      >
        {/* Header */}
        <div className="w-full flex flex-col items-center" style={{ gap: '16px' }}>
          <h2
            className="font-geist w-full text-center text-[28px] leading-[34px] md:text-[48px] md:leading-[48px]"
            style={{
              fontWeight: 600,
              color: isDark ? '#E2E8F0' : '#314158',
            }}
          >
            {t('landing.testimonials.title')}
          </h2>
          <p
            className="font-geist w-full text-center text-base md:text-xl"
            style={{
              fontWeight: 400,
              lineHeight: '28px',
              color: isDark ? '#E2E8F0' : '#314158',
            }}
          >
            {t('landing.testimonials.subtitle')}
          </p>
        </div>

        {/* Testimonial Cards */}
        <div className="w-full grid grid-cols-1 md:grid-cols-3" style={{ gap: '24px' }}>
          {testimonials.map((testimonial, index) => (
            /* Gradient border wrapper */
            <div
              key={index}
              style={{
                padding: '1px',
                borderRadius: '24px',
                background: isDark
                  ? 'linear-gradient(135deg, #162456 0%, #3C0366 50%, #510424 100%)'
                  : 'linear-gradient(135deg, #DBEAFE 0%, #F3E8FF 50%, #E9D5FF 100%)',
              }}
            >
              {/* Card inner */}
              <div
                className="flex flex-col items-start h-full"
                style={{
                  padding: '24px',
                  gap: '16px',
                  background: isDark
                    ? 'radial-gradient(100% 257.3% at 0% 100%, #0F172B 90%, #3C0366 100%)'
                    : 'radial-gradient(100% 257.3% at 0% 100%, #FFFFFF 90%, #FAF5FF 100%)',
                  borderRadius: '23px',
                }}
              >
                {/* Quote text */}
                <p
                  className="font-geist"
                  style={{
                    fontWeight: 400,
                    fontSize: '18px',
                    lineHeight: '28px',
                    color: isDark ? '#CAD5E2' : '#45556C',
                    flex: '1 0 auto',
                  }}
                >
                  {t(testimonial.quoteKey)}
                </p>

                {/* Author row + quote icon */}
                <div className="flex items-center w-full" style={{ gap: '8px' }}>
                  {/* Avatar */}
                  <img
                    src={testimonial.avatar}
                    alt={t(testimonial.nameKey)}
                    className="object-cover shrink-0"
                    style={{ width: '32px', height: '32px', borderRadius: '12px' }}
                  />
                  {/* Name & title */}
                  <div className="flex flex-col flex-1 min-w-0">
                    <p
                      className="font-geist"
                      style={{
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: isDark ? '#E2E8F0' : '#314158',
                      }}
                    >
                      {t(testimonial.nameKey)}
                    </p>
                    <p
                      className="font-geist"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: isDark ? '#90A1B9' : '#62748E',
                      }}
                    >
                      {t(testimonial.titleKey)}
                    </p>
                  </div>
                  {/* Quote icon with gradient */}
                  <div className="shrink-0" style={{ width: '40px', height: '40px' }}>
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 40 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <defs>
                        <linearGradient
                          id={`quoteGrad${index}`}
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="0%"
                        >
                          <stop offset="0%" stopColor={isDark ? '#1C398E' : '#DBEAFE'} />
                          <stop offset="100%" stopColor={isDark ? '#59168B' : '#F3E8FF'} />
                        </linearGradient>
                      </defs>
                      <path
                        d="M13.3333 20H8.33333C8.33333 15.4 11.9333 11.6667 16.3333 11.6667V14.1667C13.5733 14.1667 11.3333 16.4067 11.3333 19.1667V20H13.3333C14.7133 20 15.8333 21.12 15.8333 22.5V27.5C15.8333 28.88 14.7133 30 13.3333 30H10.8333C9.45333 30 8.33333 28.88 8.33333 27.5V22.5V20ZM30.8333 20H25.8333C25.8333 15.4 29.4333 11.6667 33.8333 11.6667V14.1667C31.0733 14.1667 28.8333 16.4067 28.8333 19.1667V20H30.8333C32.2133 20 33.3333 21.12 33.3333 22.5V27.5C33.3333 28.88 32.2133 30 30.8333 30H28.3333C26.9533 30 25.8333 28.88 25.8333 27.5V22.5V20Z"
                        fill={`url(#quoteGrad${index})`}
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <Link
          to="/registration"
          className="gradient-btn inline-flex justify-center items-center bg-fc-gradient-btn text-white font-geist"
          style={{
            fontWeight: 500,
            fontSize: '16px',
            lineHeight: '24px',
            padding: '12px 16px',
            borderRadius: '12px',
          }}
        >
          {t('landing.testimonials.cta')}
        </Link>
      </div>
    </section>
  );
};

export default TestimonialsSection;
